// src/hooks/useGameEngine.js
import { useState, useEffect, useMemo, useRef } from 'react';
import { playSound } from '../utils/sounds';

export const useGameEngine = (userProgress, saveToLocal, setView) => {
  // --- CONFIGURACIÓN ---
  const MAX_LIVES = 5;
  const BACKPACK_CAP = 10;

  const [perfectSession, setPerfectSession] = useState(true);

  // --- ESTADOS DEL JUEGO ---
  const [lesson, setLesson] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [solvedQuestions, setSolvedQuestions] = useState([]);
  const [sessionXP, setSessionXP] = useState(0);

  const [feedback, setFeedback] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sentenceWords, setSentenceWords] = useState([]);
  const [userSentence, setUserSentence] = useState([]);

  const [hasFailedCurrent, setHasFailedCurrent] = useState(false);
  const [lives, setLives] = useState(MAX_LIVES);
  const [showGameOver, setShowGameOver] = useState(false);
  // Añade esto junto a tus otros useRef
const sessionStartTime = useRef(null);

  // --- ESTADOS DE MASCOTA Y RACHAS ---
  const [streak, setStreak] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);
  const [mascotMessage, setMascotMessage] = useState({ text: "", type: "" });

  // ✅ LÓGICA DE 3 TIPOS DE AVATARES
  const activeSkin = useMemo(
    () => (userProgress?.currentSkin || 'bribark'),
    [userProgress?.currentSkin]
  );

  const baseMascot = useMemo(
    () => (String(activeSkin).startsWith('dolly') ? 'dollybelle' : 'bribark'),
    [activeSkin]
  );

  // --- UTILIDADES INTERNAS ---
  const timeoutRef = useRef(null);

const showMascot = (messages, duration = 5000) => {
  const chosen = Array.isArray(messages)
    ? messages[Math.floor(Math.random() * messages.length)]
    : messages;

  // Cancela el timeout previo si existe
  if (timeoutRef.current) clearTimeout(timeoutRef.current);

  setMascotMessage(chosen);

  if (duration !== null) {
    timeoutRef.current = setTimeout(
      () => setMascotMessage({ text: "", type: "" }),
      duration
    );
  }
};
const dismissMascot = () => {
  setMascotMessage({ text: "", type: "" });
};
  const showMascotAfter = (delayMs, message, duration = 3000) => {
    setTimeout(() => showMascot(message, duration), delayMs);
  };

  const initOrderQuestion = (question) => {
  let correctWords = [];

  // 1️⃣ Obtener las palabras correctas (lógica actual)
  if (Array.isArray(question.words)) {
    correctWords = question.words;
  } else if (typeof question.a === 'string' && question.a.trim()) {
    correctWords = question.a.split(' ').filter(w => w.trim());
  } else if (typeof question.answer === 'string' && question.answer.trim()) {
    correctWords = question.answer.split(' ').filter(w => w.trim());
  }

  // Validación: Si no hay palabras correctas, abortar
  if (correctWords.length === 0) {
    console.error("❌ Error: La pregunta no tiene palabras para ordenar", question);
    setSentenceWords([]);
    setUserSentence([]);
    return; 
  }

  // 2️⃣ Añadir distractores (si existen)
  const distractors = Array.isArray(question.extra) ? question.extra : [];
  
  // Crear el pool completo: palabras correctas + distractores
  const fullPool = [...correctWords, ...distractors];

  // 3️⃣ Barajar el pool completo con Fisher-Yates
  const shuffled = [...fullPool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  // 4️⃣ Establecer estados
  setSentenceWords(shuffled);
  setUserSentence([]);

  // 5️⃣ Log para debugging
  if (distractors.length > 0) {
    console.log(`✅ Pregunta con distractores:`, {
      correctas: correctWords.length,
      distractores: distractors.length,
      total: fullPool.length
    });
  }
};


  useEffect(() => {
    const currentQ = lesson?.questions?.[qIdx];
    if (!currentQ) return;

    if (currentQ.type === 'order') {
      initOrderQuestion(currentQ);
    } else {
      setSentenceWords([]);
      setUserSentence([]);
    }
  }, [qIdx, lesson]);

  // --- ACCIONES PÚBLICAS ---
  // src/hooks/useGameEngine.js

const startSet = (set) => {

  // 1. Validación de datos
  if (!set || !set.questions || set.questions.length === 0) {
    console.error("❌ Error: Set inválido o sin preguntas", set);
    return;
  }

  // 2. Crear una copia profunda del set (evita mutaciones)
  let processedSet = JSON.parse(JSON.stringify(set));
  sessionStartTime.current = Date.now();

  // 3. ⚡ INYECCIÓN DE CONTEXTO ⚡
  // Solo inyectar si:
  // - Existe 'context' en el set
  // - No hay ya una pantalla 'isAutoIntro' (evita duplicados)
  const hasContext = processedSet.context && processedSet.context.trim().length > 0;
  const alreadyHasIntro = processedSet.questions.some(q => q.isAutoIntro === true);

  if (hasContext && !alreadyHasIntro) {
    console.log("📖 Inyectando pantalla de contexto automático...");
    
    const introPassage = {
      type: "passage",
      title: processedSet.title || "Introducción",
      passage: processedSet.context,
      isAutoIntro: true, // Bandera para evitar duplicados
      id: `intro_${processedSet.id || Date.now()}` // ID único
    };

    // Insertar al inicio del array de preguntas
    processedSet.questions = [introPassage, ...processedSet.questions];
    
    console.log(`✅ Contexto inyectado. Total preguntas: ${processedSet.questions.length}`);
  } else if (alreadyHasIntro) {
    console.log("⚠️ Ya existe una intro, no se inyecta de nuevo.");
  } else {
    console.log("ℹ️ Este set no tiene contexto, continuando normal.");
  }

  // 4. Resetear estados del juego
  if (lives <= 0) setLives(MAX_LIVES);
  
  setLesson(processedSet); // ✅ Usar el set procesado
  setQIdx(0);
  setScore(0);
  setSolvedQuestions([]);
  setSessionXP(0);
  setFeedback(null);
  setSelected(null);
  setShowGameOver(false);
  setHasFailedCurrent(false);
  setWrongStreak(0);
  setStreak(0);
  setPerfectSession(true);

  // 5. Inicializar pregunta de orden si es necesaria
  const firstQuestion = processedSet.questions[0];
  if (firstQuestion && firstQuestion.type === 'order') {
    initOrderQuestion(firstQuestion);
  }

  // 6. Navegar a la vista de quiz
  setView('quiz');
};

  const refillLives = () => {
    setLives(MAX_LIVES);
    setShowGameOver(false);
    setQIdx(0);
    setScore(0);
    setSessionXP(0);
    setFeedback(null);
    setHasFailedCurrent(false);
    setWrongStreak(0);
    setStreak(0);
    setPerfectSession(true);
  };

  const retryQuestion = () => {
    setFeedback(null);
    setSelected(null);
    if (lesson.questions[qIdx].type === 'order') {
      setUserSentence([]);
      initOrderQuestion(lesson.questions[qIdx]);
    }
  };

  const gainLife = () => {
    if (lives < BACKPACK_CAP) {
      setLives(l => l + 1);
    }
  };

  const prevQuestion = () => {
    if (qIdx > 0) {
      const prevQ = lesson.questions[qIdx - 1];
      setQIdx(qIdx - 1);
      setFeedback(null);
      setSelected(null);
      if (prevQ.type === 'order') initOrderQuestion(prevQ);
    }
  };

const nextQuestion = () => {
    if (lives === 0) return;

    if (qIdx + 1 < lesson.questions.length) {
      // ... (Lógica de navegación que ya tienes)
      const nextQ = lesson.questions[qIdx + 1];
      setQIdx(qIdx + 1);
      setFeedback(null);
      setSelected(null);
      setHasFailedCurrent(false);
      if (nextQ.type === 'order') initOrderQuestion(nextQ);
    } else {
      // --- FINAL DE LA LECCIÓN ---
     // ✅ DESPUÉS - Excluye passages del conteo
const evaluableQuestions = lesson.questions.filter(q => q.type !== 'passage');
const totalQuestions = evaluableQuestions.length;

      const percentage = (score / totalQuestions) * 100;
      const isPassed = percentage >= 70;

      if (isPassed) {
        playSound('win');
// ⏱️ Calculamos minutos transcurridos (mínimo 1 minuto para no guardar ceros)
      const timeSpentMs = Date.now() - sessionStartTime.current;
      const sessionMinutes = Math.max(1, Math.round(timeSpentMs / 60000));
        // ✅ LÓGICA DEL BONO:
        // Si la sesión es perfecta (perfectSession es true), sumamos 50.
        const PERFECT_BONUS = 50;
        const finalXP = perfectSession ? (sessionXP + PERFECT_BONUS) : sessionXP;

        // ✅ Enviamos 'finalXP' en lugar de 'sessionXP'
        saveToLocal(score, lesson.id, finalXP, sessionMinutes, totalQuestions);
      } else {
        playSound('gameover');
      }
      setView('results');
    }
  };

  // --- LÓGICA DE RESPUESTAS (CORAZÓN DEL SISTEMA) ---
 const processAnswer = (isCorrect) => {
  // ✅ 1. PRIMERO definir currentQ
  const currentQ = lesson.questions[qIdx];
  
  const backpackSkin = `${baseMascot}mochila`;
  const teacherSkin = Math.random() < 0.5 ? 'bribarklibro' : 'dollybellelibro';

  if (isCorrect) {
    const newStreak = streak + 1;
    const isAlreadySolved = solvedQuestions.includes(qIdx);

    // Lógica de Multiplicador XP
    let multiplier = 1;
    if (newStreak >= 50) multiplier = 3;
    else if (newStreak >= 10) multiplier = 2;

    if (!isAlreadySolved && !hasFailedCurrent) {
      setScore(s => s + 1);
      const xpGained = 10 * multiplier;
      setSessionXP(prev => prev + xpGained);
      setSolvedQuestions(prev => [...prev, qIdx]);
    }

    setFeedback('correct');
    setWrongStreak(0);
    setStreak(newStreak);

    const multiplierMsg = multiplier === 3
        ? { text: "⚡ XP x3 ACTIVADO", type: activeSkin }
        : { text: "⚡ XP x2 ACTIVADO", type: activeSkin };

    const shouldAnnounceMultiplier = (multiplier > 1) && (
      newStreak === 10 || newStreak === 12 || newStreak === 50 || newStreak === 53
    );

    // ✅ TODAS LAS RACHAS PRIMERO (con if/else if)
    if (newStreak === 53) {
      playSound('streak');
      showMascot([{ text: "GOD MODE ON 🔥 53+ seguidas.", type: activeSkin }], 9000);
      if (shouldAnnounceMultiplier) showMascotAfter(9200, multiplierMsg, 2500);
    }
    else if (newStreak === 50) {
      playSound('streak');
      showMascot([{ text: "¡50 seguidas! Estás en otro nivel. 🔥", type: activeSkin }], 5000);
      if (shouldAnnounceMultiplier) showMascotAfter(5200, multiplierMsg, 2500);
    }
    else if (newStreak === 33) {
      playSound('streak');
      showMascot([{ text: "¡33 seguidas! Nivel LEYENDA 😎🔥", type: activeSkin }], 7000);
    }
    else if (newStreak === 23) {
      playSound('streak');
      showMascot([{ text: "¡23 seguidas! Vas volando 🚀", type: activeSkin }], 5000);
    }
    else if (newStreak === 13) {
      playSound('streak');
      showMascot([{ text: "¡13 seguidas! ¡Wow! 🚀", type: activeSkin }], 5000);
    }
    else if (newStreak === 12) {
      playSound('streak');
      showMascot([{ text: "¡12 seguidas! Buen ritmo 👏", type: activeSkin }], 5000);
      if (shouldAnnounceMultiplier) showMascotAfter(5200, multiplierMsg, 2500);
    }
    else if (newStreak === 10) {
      if (!hasFailedCurrent && lives < BACKPACK_CAP) {
        setLives(l => l + 1);
        playSound('streak');
        showMascot([{ text: "🎒 +1 vida a la mochila (racha 10)", type: backpackSkin }], 4500);
      } else {
        playSound('streak');
        showMascot([{ text: "¡10 seguidas! Buen ritmo. 👏", type: activeSkin }], 4500);
      }
      if (shouldAnnounceMultiplier) showMascotAfter(4700, multiplierMsg, 2500);
    }
    else if (newStreak === 5 || newStreak === 15 || newStreak === 20 || newStreak === 25) {
      if (!hasFailedCurrent && lives < BACKPACK_CAP) {
        setLives(l => l + 1);
        playSound('streak');
        showMascot([{ text: `🎒 +1 vida a la mochila (racha ${newStreak})`, type: backpackSkin }], 4000);
      }
    }
    // ✅ 3. DESPUÉS DE TODAS LAS RACHAS: successTip
   else if (currentQ.successTip || currentQ.successtip) {
  const tipText = currentQ.successTip || currentQ.successtip;
  showMascot({ 
    text: tipText, 
    type: activeSkin,       // ← imagen correcta del avatar
    variant: 'successtip'   // ← señal para QuizView de bloquear
  }, null);
    }
    else if (currentQ.tip && currentQ.tip.startsWith('TIP:')) {
      showMascot([{ text: currentQ.tip, type: activeSkin }], 5000);
    }

  } else {
    // INCORRECTO
    setFeedback("wrong");
    setPerfectSession(false);
    setStreak(0);
    setHasFailedCurrent(true);

    const newLives = lives - 1;
    setLives(newLives);

    if (newLives === 0) {
      playSound('gameover');
      setTimeout(() => setShowGameOver(true), 2000);
    }

    const newWrong = wrongStreak + 1;
    setWrongStreak(newWrong);

    if (currentQ.tip && !currentQ.tip.startsWith('TIP:')) {
      showMascot([{ text: `💡 ${currentQ.tip}`, type: teacherSkin }], 6000);
    } else if (newWrong === 2) {
      showMascot([{ text: "Respira. Lee con calma. 👍", type: teacherSkin }], 5000);
    }
  }
};
 const normalizeAnswer = (s) =>
    String(s)
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[!"#$%&()*+,\-.\/:;<=>?@[\\\]^_`{|}~]/g, "")
      .replace(/\s+/g, " ");

 const handleSimpleAnswer = (inputData) => {
  if (feedback) return;

  const currentQ = lesson.questions[qIdx];
  let userFinalText = "";
  let isMultiGap = false;

  // ✅ PASO 1: Determinar el tipo de input y extraer el valor
  if (typeof inputData === 'object' && inputData !== null && !Array.isArray(inputData)) {
    // Verificar si es un objeto de múltiples huecos {0: "val1", 1: "val2"}
    const keys = Object.keys(inputData);
    const hasNumericKeys = keys.every(k => !isNaN(k));
    
    if (hasNumericKeys && keys.length > 0) {
      // ✅ Es un ejercicio de múltiples huecos
      isMultiGap = true;
      const totalGaps = (currentQ.q.match(/___/g) || []).length;
      const parts = [];
      
      for (let i = 0; i < totalGaps; i++) {
        parts.push(inputData[i] || "");
      }
      
      userFinalText = parts.join(" / ");
    } else if (inputData.text) {
      // ✅ Es una opción de MC con formato {text: "...", ...}
      userFinalText = inputData.text;
    } else {
      // Fallback: intentar convertir a string
      userFinalText = String(inputData);
    }
  } else {
    // ✅ Es un string simple
    userFinalText = String(inputData || "");
  }

  // Guardar para mostrar en la UI
  setSelected(userFinalText);

  // ✅ PASO 2: Normalizar la respuesta del usuario
  const userClean = normalizeAnswer(userFinalText);

  // ✅ PASO 3: Recopilar todas las respuestas válidas
  let allValidAnswers = [];

  // 3.1: Obtener respuestas base
  if (Array.isArray(currentQ.a)) {
    allValidAnswers.push(...currentQ.a);
  } else {
    allValidAnswers.push(currentQ.a);
  }

  // 3.2: Añadir respuestas alternativas (alts)
  if (currentQ.alts && Array.isArray(currentQ.alts)) {
    allValidAnswers.push(...currentQ.alts);
  }

  // ✅ PASO 4: Validar según el tipo de pregunta
  let isCorrect = false;

  if (isMultiGap) {
    // Para multi-gap, comparar el formato completo "val1 / val2 / val3"
    isCorrect = allValidAnswers
      .map(a => normalizeAnswer(String(a)))
      .some(validAnswer => validAnswer === userClean);
  } else {
    // Para preguntas simples, permitir coincidencia con cualquier respuesta válida
    // También soportar respuestas con "/" (ej: "apple/manzana")
    isCorrect = allValidAnswers
      .map(a => normalizeAnswer(String(a)))
      .some(validAnswer => {
        // Comparación exacta
        if (validAnswer === userClean) return true;
        
        // Comparación con variantes separadas por "/"
        const variants = validAnswer.split('/').map(v => v.trim());
        return variants.some(variant => normalizeAnswer(variant) === userClean);
      });
  }

  processAnswer(isCorrect);
};


  const handleWordClick = (word, index, type) => {
    if (feedback) return;
    if (type === 'pool') {
      const newPool = [...sentenceWords];
      newPool.splice(index, 1);
      setSentenceWords(newPool);
      setUserSentence([...userSentence, word]);
    } else {
      const newSentence = [...userSentence];
      newSentence.splice(index, 1);
      setUserSentence(newSentence);
      setSentenceWords([...sentenceWords, word]);
    }
  };
// Añadir después de la función handleWordClick


  const checkOrderAnswer = () => {
    if (feedback) return;

    const currentQ = lesson.questions[qIdx];
    const formedSentence = userSentence.join(" ");

    const userClean = normalizeAnswer(formedSentence);
    const correctClean = normalizeAnswer(currentQ.a);

    processAnswer(userClean === correctClean);
  };
const clearAllWords = () => {
  if (feedback) return;
  
  // Restaurar todas las palabras al pool de una vez
  setSentenceWords(prev => [...prev, ...userSentence]);
  setUserSentence([]);
};
  return {
    game: {
      perfectSession,
      activeSkin,  
      baseMascot,
      lesson,
      qIdx,
      score,
      sessionXP,
      feedback,
      selected,
      sentenceWords,
      userSentence,
      lives,
      streak,
      showGameOver,
      BACKPACK_CAP
    },
    mascotMessage,
    setMascotMessage,
    actions: {
      startSet,
      refillLives,
      retryQuestion,
      nextQuestion,
      handleSimpleAnswer,
      handleWordClick,
      checkOrderAnswer,
      gainLife,
      prevQuestion,
      clearAllWords,
      dismissMascot,
      showMascot
    }
  };
};
